import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DsFormsModule, DsTooltipDirectiveModule } from '@bmw-ds/components';
import { random } from 'lodash';
import { NGXLogger } from 'ngx-logger';

import { useI18n } from '../../../i18n/services/i18n.service';
import { lensProxy } from '../../../utils/lensProxy/lensProxy';
import { ConsentCheckboxGroup, ConsentSelection } from '../../model/consent-checkbox';
import { LegalExpandableComponent } from '../legal-text/legal-expandable.component';

@Component({
  selector: 'cdf-consent-picker',
  imports: [DsFormsModule, FormsModule, DsTooltipDirectiveModule, LegalExpandableComponent],
  template: `
    <table>
      <thead>
        <tr>
          <!-- click the title 10 times to switch the grouping by each further click -->
          <th (click)="debugMode.set(debugMode() + 1)"></th>
        </tr>
      </thead>
      <tbody>
        <!-- select-all buttons -->
        @if ((isInitialConsentCreationMode() ? consentCheckboxGroups().length : checkboxCount()) > 1) {
          <tr>
            <td class="expand">
              <!-- label needed for full click area. Only checkbox and text would be clickable otherwise -->
              <label>
                <ds-form-field>
                  <ds-checkbox-group size="lg">
                    <input
                      ds-input
                      type="checkbox"
                      [id]="consentInputId('all')"
                      [ngModel]="allSelected()"
                      [indeterminate]="indeterminate()"
                      (ngModelChange)="setAll($event)"
                      [disabled]="isCheckboxDisabled('all') || !!readonlyTooltip()"
                      [ds-tooltip]="i18n('readonlyTooltip')"
                      [dsTooltipConfig]="{
                        disabled: !readonlyTooltip(),
                        origin: 'bottom',
                        showTriggerDelay: 500,
                      }"
                    />
                    <label ds-label [for]="consentInputId('all')">{{ i18n('consent.all') }}</label>
                  </ds-checkbox-group>
                </ds-form-field>
              </label>
            </td>
          </tr>
        }

        <!-- multiple checkboxes per group -->
        @for (checkboxGroup of consentCheckboxGroups(); track groupIndex; let groupIndex = $index) {
          <tr>
            <td class="expand">
              @if (
                (isInitialConsentCreationMode() || checkboxGroup.checkboxes.length === 1) &&
                (debugMode() < 10 || debugMode() % 2 === 1)
              ) {
                <!-- Only one checkbox per group is shown — either because the group contains only one checkbox, or because it is the initial simple view which displays only non-implicit groups. -->
                <label>
                  <!-- label needed for full click area. Only checkbox and text would be clickable otherwise -->
                  <ds-form-field>
                    <ds-checkbox-group size="lg">
                      <input
                        ds-input
                        type="checkbox"
                        [id]="consentInputId(string(groupIndex))"
                        [ngModel]="consentSelectionGroupProxy()[string(groupIndex)].allSelected"
                        [indeterminate]="consentSelectionGroupProxy()[string(groupIndex)].someSelected"
                        (ngModelChange)="updateGroup(checkboxGroup, $event)"
                        [disabled]="isCheckboxGroupDisabled(checkboxGroup) || !!readonlyTooltip()"
                        [ds-tooltip]="readonlyTooltip()"
                        [dsTooltipConfig]="{ disabled: !readonlyTooltip(), origin: 'bottom', showTriggerDelay: 500 }"
                      />
                      <label
                        ds-label
                        [for]="consentInputId(string(groupIndex))"
                        [ds-tooltip]="readonlyTooltip()"
                        [dsTooltipConfig]="{ disabled: !readonlyTooltip(), origin: 'bottom', showTriggerDelay: 500 }"
                      >
                        @if (checkboxGroup.headline?.text) {
                          <cdf-legal-expandable [rootLegalText]="checkboxGroup.headline!" />
                        } @else {
                          {{ joinGroupLabels(checkboxGroup) }}
                        }
                      </label>
                    </ds-checkbox-group>
                  </ds-form-field>
                </label>
              } @else {
                <!-- If a group contains multiple checkboxes, a group title is shown. -->
                @if (checkboxGroup.headline?.text) {
                  <cdf-legal-expandable [rootLegalText]="checkboxGroup.headline!" />
                }
                @for (checkbox of checkboxGroup.checkboxes; track checkbox.key) {
                  <label>
                    <ds-form-field>
                      <ds-checkbox-group size="lg">
                        <input
                          ds-input
                          type="checkbox"
                          [(ngModel)]="consentSelectionProxy[checkbox.key]"
                          [id]="consentInputId(checkbox.key)"
                          [disabled]="isCheckboxDisabled(checkbox.key) || !!readonlyTooltip()"
                          [ds-tooltip]="readonlyTooltip()"
                          [dsTooltipConfig]="{
                            disabled: !readonlyTooltip(),
                            origin: 'bottom',
                            showTriggerDelay: 500,
                          }"
                        />
                        <label
                          ds-label
                          [for]="consentInputId(checkbox.key)"
                          [ds-tooltip]="readonlyTooltip()"
                          [dsTooltipConfig]="{
                            disabled: !readonlyTooltip(),
                            origin: 'bottom',
                            showTriggerDelay: 500,
                          }"
                        >
                          {{ checkbox.label }}
                        </label>
                      </ds-checkbox-group>
                    </ds-form-field>
                  </label>
                }
              }
            </td>
          </tr>
        }
      </tbody>
    </table>
  `,
  styleUrl: './consent-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentPickerComponent {
  protected readonly i18n = useI18n();
  private readonly logger = inject(NGXLogger);

  readonly consentSelection = model.required<ConsentSelection>();
  readonly consentSelectionProxy = lensProxy(this.consentSelection);
  readonly consentSelectionGroupProxy = computed(() => {
    return Object.fromEntries(
      this.consentCheckboxGroups().map((group, index) => {
        const countChecked = group.checkboxes.filter((checkbox) => this.consentSelectionProxy[checkbox.key]()).length;
        return [
          index.toString(),
          {
            allSelected: countChecked === group.checkboxes.length,
            someSelected: countChecked > 0 && countChecked < group.checkboxes.length,
          },
        ];
      }),
    );
  });

  readonly consentCheckboxGroups = input.required<ConsentCheckboxGroup[]>();
  readonly readonlyTooltip = input('');
  readonly initialConsentCreationMode = input(false);

  // signals for dealer consent
  readonly disabled = input<boolean>();
  readonly disabledExceptionUsageId = input<string>();

  readonly debugMode = signal(0);

  readonly indeterminate = computed(() => {
    const selectedCount = Object.values(this.consentSelection()).filter((selected) => selected).length;
    return selectedCount > 0 && selectedCount < this.checkboxCount();
  });

  readonly allSelected = computed(() => {
    const selectedCount = Object.values(this.consentSelection()).filter((selected) => selected).length;
    return selectedCount === this.checkboxCount();
  });

  readonly checkboxCount = computed((): number => {
    return this.consentCheckboxGroups().reduce((sum, group) => sum + group.checkboxes.length, 0);
  });

  // 5 char hex string to differentiate between multiple consent pickers
  readonly id = random(0, 1048576).toString(16);

  protected consentInputId(id: string): string {
    return `cdf-consent-picker-input-${id}-` + this.id;
  }

  protected setAll(enabled: boolean) {
    this.consentSelection.set(
      Object.fromEntries(
        this.consentCheckboxGroups().flatMap((checkboxGroup) =>
          checkboxGroup.checkboxes.map((checkbox) => [checkbox.key, enabled]),
        ),
      ),
    );
  }

  protected isCheckboxGroupDisabled(group: ConsentCheckboxGroup): boolean {
    return group.checkboxes.some((checkbox) => this.isCheckboxDisabled(checkbox.key));
  }

  protected isCheckboxDisabled(usageId: string): boolean {
    if (usageId === this.disabledExceptionUsageId()) {
      return false;
    }

    return this.disabled() ?? false;
  }

  protected updateGroup(group: ConsentCheckboxGroup, isChecked: boolean) {
    group.checkboxes.forEach((checkbox) => this.consentSelectionProxy[checkbox.key].set(isChecked));
  }

  protected joinGroupLabels(group: ConsentCheckboxGroup) {
    const labels = group.checkboxes.map((checkbox) => checkbox.label).join(', ');
    if (group.checkboxes.length > 1) {
      this.logger.warn(
        `#CDFTF0181 consent usage definition ${group.usageDefinitionId} fallback for missing primaryText executed: ${labels}`,
      );
    }
    return labels;
  }

  protected isInitialConsentCreationMode() {
    const groups = this.consentCheckboxGroups();
    return this.initialConsentCreationMode() && groups.length > 0 && !!groups[0].usageDefinitionId;
  }

  protected readonly string = String;
}
